/**
 * main.ts — Filosofia EduTool entry point.
 *
 * Boot sequence: manifest + data service → AppShell → StageRouter.
 * Fase D1: catalog browsing (AuthorListView + AuthorDetailView via StageRouter).
 */

// Styles — order matters: tokens first, then base, then layout, then components
import '@/styles/tokens-base.css';
import '@/styles/tokens-brand.css';
import '@/styles/base.css';
import '@/styles/layout.css';
import '@/styles/views.css';
import '@/styles/responsive.css';
import '@/styles/lesson-builder.css';

import { AppShell } from '@/core/AppShell';
import { applySurface } from '@/core/surface';
import type { ToolManifest } from '@/bridge/bridge-types';

// Sidebar sections
import { BrandHeader } from '@/components/sidebar/BrandHeader';
import { SystemTray } from '@/components/sidebar/SystemTray';
import { FilterPanel } from '@/components/sidebar/FilterPanel';
import { ViewNav } from '@/components/sidebar/ViewNav';

// Stage router and views
import { StageRouter } from '@/views/StageRouter';

// Data layer (Fase B — contract + implementation)
import { StaticDataService } from '@/services/StaticDataService';
import type { IDataService } from '@/services/IDataService';
import { setDataService } from '@/services/data-registry';
import { GlossarioService, setGlossarioService } from '@/services/GlossarioService';
import { termPopover } from '@/views/terms';
import { lessonBuilderService } from '@/services/lesson-builder-service';

// Load manifest from public/edutool.manifest.json
async function loadManifest(): Promise<ToolManifest> {
  const response = await fetch('/edutool.manifest.json');
  if (!response.ok) throw new Error('Failed to load edutool.manifest.json');
  return response.json() as Promise<ToolManifest>;
}

async function bootDataService(): Promise<IDataService> {
  const service = new StaticDataService();
  await service.init();
  setDataService(service);

  // Glossario lives as its own singleton (UI/presentation concern).
  setGlossarioService(new GlossarioService());

  const [authors, concepts, tappe] = await Promise.all([
    service.listAuthors({}, 'it'),
    service.listConcepts('it'),
    service.listTappe({}, 'it'),
  ]);
  console.info(
    `[filosofia] IDataService ready — catalog: ${authors.length} autori, ${concepts.length} concetti, ${tappe.length} tappe`,
  );

  return service;
}

async function main(): Promise<void> {
  try {
    // SUPERFICIE prima di tutto: la scala LIM (html[data-surface=lim]) deve
    // esserci già quando la shell monta (sidebar chiusa, scala proiezione).
    const surface = applySurface();

    const [manifest] = await Promise.all([loadManifest(), bootDataService()]);

    const shell = new AppShell({
      manifest,
      sections: {
        brand: BrandHeader,
        nav: ViewNav,
        list: FilterPanel,
        tray: SystemTray,
      },
      stageRenderer: StageRouter,
      defaultSidebarOpen: true,
    });

    shell.boot().catch(console.error);

    // Install the contextual glossary popover after the shell is mounted.
    termPopover.install('it');

    // Lesson-builder "+" capture (docente). Gated internamente su role/userId
    // dall'URL; disattivo in modalità LIM (in classe il tool resta pulito).
    if (surface !== 'lim') lessonBuilderService.init(manifest.id, manifest.subject);

    // "← Eduverse": index.html punta alla produzione, che è il default giusto
    // quando il tool viene aperto da un link statico. Se invece è stato l'hub
    // ad aprirci, ci ha passato il proprio indirizzo: torniamo a QUELL'hub
    // (dev→dev, prod→prod) invece di sbalzare l'utente su un'altra istanza.
    // Su LIM il ritorno naturale è il menu Aula, non la home.
    const back = document.querySelector<HTMLAnchorElement>('.eduverse-back');
    if (back) {
      const hubUrl = new URLSearchParams(window.location.search).get('hubUrl');
      if (hubUrl) {
        const base = hubUrl.replace(/\/+$/, '');
        back.href = surface === 'lim' ? `${base}/lim` : base;
      }
      if (surface === 'lim') {
        back.innerHTML = '<span aria-hidden="true">←</span> Menu';
        back.title = 'Torna al menu Aula';
      }
    }
  } catch (err) {
    console.error('[main] boot failed:', err);
  }
}

main().catch(console.error);
